/**
 * Eigenbau: Persönliche Einstellungen — Zeitzone und Tageszusammenfassung.
 *
 * Zeitzone steuert „heute fällig“ und Kapazitätstage; die API
 * `personen.setzeZeitzone` lag fertig, fehlte aber in der Oberfläche.
 * Die Tageszusammenfassung kommt aus der Einstellungs-Registry.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findeEinstellung } from "@cubic/shared";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Schalter } from "@/components/entwurf/Schalter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { api } from "@/lib/api";
import { useIch } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  EinstellungsZeile,
  Erfolgsmeldung,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
  zeitzonenMitBrowser,
} from "./gemeinsam";

const digestDefinition = findeEinstellung("digest_opt_in")!;

export function EinstellungenPersoenlich() {
  const client = useQueryClient();
  const ich = useIch();
  const personId = ich.data?.personId;

  const person = useQuery({
    queryKey: ["cubic", "person", personId],
    queryFn: () => api.personen.lies.query({ id: personId! }),
    enabled: Boolean(personId),
  });

  const personDigest = useQuery({
    queryKey: ["cubic", "einstellung", "digest_opt_in", "person"],
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: "digest_opt_in",
        bereich: "person",
      }),
  });

  const [zeitzone, setzeZeitzoneLokal] = useState<string | null>(null);
  const [digest, setzeDigest] = useState<string | null>(null);
  const [ok, setzeOk] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<Meldung | null>(null);

  useEffect(() => {
    if (person.data?.zeitzone && zeitzone === null) {
      setzeZeitzoneLokal(person.data.zeitzone);
    }
  }, [person.data?.zeitzone, zeitzone]);

  useEffect(() => {
    if (personDigest.data !== undefined && digest === null) {
      setzeDigest(personDigest.data);
    }
  }, [personDigest.data, digest]);

  const speichereZeitzone = useMutation({
    mutationFn: (wert: string) =>
      api.personen.setzeZeitzone.mutate({ id: personId!, zeitzone: wert }),
    onSuccess: async (daten) => {
      setzeFehler(null);
      setzeOk("Zeitzone gespeichert.");
      setzeZeitzoneLokal(daten.zeitzone);
      await client.invalidateQueries({ queryKey: ["cubic", "person", personId] });
    },
    onError: (err) => {
      setzeOk(null);
      setzeFehler(meldungFuer(err, "Zeitzone konnte nicht gespeichert werden."));
    },
  });

  const speichereDigest = useMutation({
    mutationFn: (wert: string) =>
      api.einstellungen.setzen.mutate({
        schluessel: "digest_opt_in",
        wert,
        bereich: "person",
      }),
    onSuccess: async (daten) => {
      setzeFehler(null);
      setzeOk("Tageszusammenfassung gespeichert.");
      setzeDigest(daten.wert);
      await client.invalidateQueries({
        queryKey: ["cubic", "einstellung", "digest_opt_in"],
      });
    },
    onError: (err) => {
      setzeOk(null);
      setzeFehler(
        meldungFuer(err, "Tageszusammenfassung konnte nicht gespeichert werden."),
      );
    },
  });

  const zeitzoneWert = zeitzone ?? person.data?.zeitzone ?? "Europe/Berlin";
  const digestAn =
    (digest ?? personDigest.data ?? digestDefinition.standard) === "true";
  const zeitzonen = zeitzonenMitBrowser();
  if (!zeitzonen.includes(zeitzoneWert)) {
    zeitzonen.unshift(zeitzoneWert);
  }

  const ladenFehler = ich.error ?? person.error ?? personDigest.error;
  const laedt = ich.isLoading || person.isLoading || personDigest.isLoading;

  return (
    <EinstellungenHuelle
      aktiv="persoenlich"
      beschreibung="Was nur dich betrifft: Zeitzone für Fristen und die tägliche Zusammenfassung."
    >
      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      {!fehler && ladenFehler ? (
        <FehlerBlock
          fehler={ladenFehler}
          fallback="Persönliche Einstellungen konnten nicht geladen werden."
          onErneut={() => {
            void Promise.all([
              ich.refetch(),
              person.refetch(),
              personDigest.refetch(),
            ]);
          }}
        />
      ) : null}
      <Erfolgsmeldung text={ok} />

      <EinstellungsAbschnitt
        titel="Konto"
        beschreibung="Name und E-Mail kommen aus Authentik und lassen sich hier nicht ändern."
      >
        {laedt ? (
          <LadenZeile />
        ) : (
          <div className="space-y-1 py-2">
            <p className="text-[14.5px] text-tinte">
              {person.data?.name ?? "—"}
            </p>
            <p className="font-mono text-[12px] text-tinte3">
              {person.data?.email ?? "—"}
            </p>
          </div>
        )}
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Zeitrechnung"
        beschreibung="„Heute fällig“, Kapazitätstage und Kalendergrenzen folgen dieser Zone — nicht der des Servers."
      >
        <EinstellungsZeile
          label="Zeitzone"
          hinweis="Wirkt auf Fristen, die Übersicht und die Tageskapazität."
        >
          <Select
            value={zeitzoneWert}
            onValueChange={(wert) => {
              setzeZeitzoneLokal(wert);
              /*
                Die Erfolgsmeldung wird hier **nicht** gelöscht.

                Vorher stand hier `setzeOk(null)`. Beim zweiten Speichern
                verschwand die Meldung dadurch für die Dauer des Rundlaufs —
                gemessen 465 ms — und kam mit demselben Wortlaut zurück. Das
                war das Flackern: nicht ein Neuzeichnen, sondern eine Lücke.

                Eine überholte Bestätigung ersetzt `onSuccess` ohnehin, und
                schlägt das Speichern fehl, räumt `onError` sie weg. Nur der
                Fehler ist sofort hinfällig, sobald man etwas ändert.
              */
              setzeFehler(null);
            }}
            disabled={laedt || Boolean(ladenFehler)}
          >
            <SelectTrigger className="w-64" aria-label="Zeitzone">
              <SelectValue placeholder="Zeitzone wählen" />
            </SelectTrigger>
            <SelectContent>
              {zeitzonen.map((z) => (
                <SelectItem key={z} value={z}>
                  {z}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => speichereZeitzone.mutate(zeitzoneWert)}
            disabled={
              speichereZeitzone.isPending ||
              laedt ||
              Boolean(ladenFehler) ||
              zeitzoneWert === person.data?.zeitzone
            }
            isLoading={speichereZeitzone.isPending}
          >
            Speichern
          </Button>
        </EinstellungsZeile>
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Benachrichtigungen"
        beschreibung={digestDefinition.beschreibung}
      >
        <EinstellungsZeile
          label={digestDefinition.label}
          hinweis="Eine E-Mail statt vieler Einzelmeldungen — wenn der Versand eingerichtet ist."
        >
          <Schalter
            an={digestAn}
            ariaLabel={digestDefinition.label}
            onKlick={() => {
              const naechster = digestAn ? "false" : "true";
              setzeDigest(naechster);
              // Meldung stehen lassen — siehe Zeitzone oben.
              setzeFehler(null);
              speichereDigest.mutate(naechster);
            }}
          />
          <span className="font-mono text-[11.5px] text-tinte3">
            {digestAn ? "An" : "Aus"}
          </span>
        </EinstellungsZeile>
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
